import { isActivePaidSubscription } from '@/components/subscription/plan-choice/plan-choice-offer';
import type { OrganizationBillingContext } from '@fluenceur/types';
import type { MetaPixelEvent, MetaPixelParams } from './meta-pixel';

/**
 * A subscription conversion is reported only for a plan the brand started from this
 * browser, so existing subscribers, renewals and a teammate's later visit never count.
 *
 * The intent is written when the charge is accepted, before the brand leaves for Stripe
 * Checkout or the Shopify approval page, and survives that round trip in localStorage.
 * It is consumed the first time the billing context shows that plan active.
 */
const INTENT_KEY = 'fluenceur_meta_subscription_intent';
const INTENT_MAX_AGE_MS = 7 * 86_400_000;

interface SubscriptionIntent {
  organizationId: string;
  planId: string;
  at: number;
}

export interface SubscriptionConversion {
  event: Extract<MetaPixelEvent, 'Purchase' | 'StartTrial'>;
  params: MetaPixelParams;
  eventId: string;
}

export function rememberSubscriptionIntent(
  organizationId: string,
  planId: string,
  at = Date.now()
) {
  try {
    localStorage.setItem(INTENT_KEY, JSON.stringify({ organizationId, planId, at }));
  } catch {
    // Without storage the conversion is simply not reported.
  }
}

function readIntent(): SubscriptionIntent | null {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(INTENT_KEY) ?? 'null');
    if (
      typeof value === 'object' &&
      value !== null &&
      typeof (value as SubscriptionIntent).organizationId === 'string' &&
      typeof (value as SubscriptionIntent).planId === 'string' &&
      typeof (value as SubscriptionIntent).at === 'number'
    ) {
      return value as SubscriptionIntent;
    }
  } catch {
    /* Unreadable intent: nothing to report. */
  }
  return null;
}

function forgetIntent() {
  try {
    localStorage.removeItem(INTENT_KEY);
  } catch {
    /* Nothing to clean. */
  }
}

/** Amounts are minor units excluding tax; the first invoice is the money actually paid. */
function amountOf(context: OrganizationBillingContext, planId: string) {
  if (context.firstInvoice) {
    return {
      value: context.firstInvoice.amountPaid / 100,
      currency: context.firstInvoice.currency,
    };
  }
  if (context.paidCheckout?.planId === planId) {
    return {
      value: context.paidCheckout.unitAmount / 100,
      currency: context.paidCheckout.currency,
    };
  }
  return {};
}

/** Returns the conversion to report at most once, and consumes the intent when it does. */
export function takeSubscriptionConversion(
  context: OrganizationBillingContext,
  now = Date.now()
): SubscriptionConversion | null {
  const intent = readIntent();
  if (!intent) return null;
  if (now - intent.at > INTENT_MAX_AGE_MS) {
    forgetIntent();
    return null;
  }
  const subscription = context.subscription;
  if (
    intent.organizationId !== context.organizationId ||
    !subscription ||
    subscription.planId !== intent.planId ||
    !isActivePaidSubscription(subscription)
  ) {
    return null;
  }

  forgetIntent();
  const inTrial = !!subscription.trialEndsAt && Date.parse(subscription.trialEndsAt) > now;
  return {
    event: inTrial ? 'StartTrial' : 'Purchase',
    params: inTrial
      ? { content_name: subscription.planId }
      : { ...amountOf(context, subscription.planId), content_name: subscription.planId },
    eventId: `subscription:${subscription.id}`,
  };
}
